import './manager-outcome-section.scss'

import {
  useEffect,
  useId,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  Completed,
  Save as SaveIcon,
} from '@vedmarket/ui-kit'
import moment from 'moment'

import {
  APPLICATION_OUTCOME,
} from 'config/constants'
import { formatReferenceCopy } from 'utils/typography'

const ManagerOutcomeSection = ({
  app,
  outcomes,
  outcome,
  outcomeNote,
  onOutcomeChange,
  onOutcomeNoteChange,
  runAction,
  onUpdateOutcome,
}: ApplicationManagerOutcomeSectionProps) => {
  const { t } = useTranslation()

  const [ outcomeEditOpen, setOutcomeEditOpen ] = useState(true)
  const reasonLabelId = useId()

  const hasSavedOutcome = app.outcome !== APPLICATION_OUTCOME.UNKNOWN
  const reasonLabel = formatReferenceCopy(t('applications__text__outcome'))
  const noteLabel = formatReferenceCopy(t('applications__text__outcomeNote'))
  const confirmedAtLabel = app.outcome_confirmed_at
    ? moment(app.outcome_confirmed_at).format('DD.MM.YYYY HH:mm')
    : null

  const summaryRows = [
    {
      label: <Typography component='span' variant='inherit' color='text.primary'>{reasonLabel}</Typography>,
      value: formatReferenceCopy(app.outcome_label),
    },
    ...(app.outcome_note?.trim() ? [ {
      label: <Typography component='span' variant='inherit' color='text.primary'>{noteLabel}</Typography>,
      value: <Typography component='span' variant='inherit' sx={{
        whiteSpace: 'pre-wrap',
      }}>{formatReferenceCopy(app.outcome_note)}</Typography>,
    } ] : []),
    ...(confirmedAtLabel ? [ {
      label: <Typography component='span' variant='inherit' color='text.primary'>{formatReferenceCopy(t('applications__text__outcomeUpdatedAt'))}</Typography>,
      value: <Typography component='time' variant='inherit' dateTime={app.outcome_confirmed_at ?? undefined}>
        {confirmedAtLabel}
      </Typography>,
    } ] : []),
  ]

  useEffect(() => {
    setOutcomeEditOpen(!hasSavedOutcome)
  }, [ hasSavedOutcome, app.outcome_confirmed_at ])

  const handleEdit = () => {
    onOutcomeChange(app.outcome)
    onOutcomeNoteChange(app.outcome_note ?? '')
    setOutcomeEditOpen(true)
  }

  const handleCancel = () => {
    onOutcomeChange(app.outcome)
    onOutcomeNoteChange(app.outcome_note ?? '')
    setOutcomeEditOpen(!hasSavedOutcome)
  }

  const handleSave = () => {
    runAction(async () => {
      await onUpdateOutcome()
      setOutcomeEditOpen(false)
    }, 'applications__message__outcomeSaved')
  }

  return (
    <Box className='application-manager-outcome-section'>
      {hasSavedOutcome && !outcomeEditOpen && (
        <>
          <Completed
            caption={formatReferenceCopy(t('applications__text__mgrStep_outcome'))}
            info={summaryRows}
            isFullRow
          />
          <Button variant='outlined' onClick={handleEdit} sx={{
            alignSelf: 'flex-start',
          }}>
            <Typography component='span' variant='inherit'>
              {formatReferenceCopy(t('actions__text__edit'))}
            </Typography>
          </Button>
        </>
      )}

      {outcomeEditOpen && (
        <>
          <FormControl fullWidth size='small'>
            <InputLabel id={reasonLabelId} shrink>
              <Typography component='span' variant='inherit' color='text.primary'>{reasonLabel}</Typography>
            </InputLabel>
            <Select
              labelId={reasonLabelId}
              label={reasonLabel}
              notched
              value={outcome}
              onChange={(e) => onOutcomeChange(e.target.value)}
            >
              {(outcomes ?? []).map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Typography component='span' variant='inherit'>{formatReferenceCopy(o.label)}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size='small'
            multiline
            minRows={2}
            InputLabelProps={{ shrink: true }}
            label={<Typography component='span' variant='inherit' color='text.primary'>{noteLabel}</Typography>}
            value={outcomeNote}
            onChange={(e) => onOutcomeNoteChange(e.target.value)}
          />
          <Box className='application-manager-outcome-section__actions'>
            <Button variant='outlined' onClick={handleCancel} sx={{
              minWidth: '238px',
              flexShrink: 0,
            }}>
              <Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('applications__text__outcomeCancel'))}
              </Typography>
            </Button>
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              disabled={!outcome}
              onClick={handleSave}
              sx={{
                flex: 1,
              }}
            >
              <Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('actions__text__save'))}
              </Typography>
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

export default ManagerOutcomeSection
